// Real API types for the Quality Control module

export type Tier = 'tier1' | 'tier2' | 'tier3' | 'unqualified' | string;
export type Intent = 'sales' | 'service' | 'ivr_only' | 'unknown' | string;
export type Disposition = 'converted' | 'interested' | 'not_interested' | 'no_answer' | 'voicemail' | string;

export interface CallClassification {
  id: string;
  callId: string;
  tier: string | null;
  primaryIntent: string | null;
  secondaryIntent: string | null;
  disposition: string | null;
  agentScore: number | null;
  summary: string | null;
  callerName: string | null;
  callerState: string | null;
  insuranceType: string | null;
  coverageNeeds: string | null;
  vehicleInfo: string | null;
  agentCompany: string | null;
  classifiedAt: string;
}

export interface FraudEvent {
  id: string;
  callId: string | null;
  buyerId: string | null;
  type: string;
  details: unknown;
  severity: string | null;
  resolved: string | null;
  createdAt: string;
}

export interface CallTranscript {
  id: string;
  callId: string;
  transcript: string;
  speakerSegments: unknown; // parsed separately
  wordCount: number | null;
  provider: string | null;
  createdAt: string;
}

export interface RealCall {
  id: string;
  tenantId: string;
  externalCallId: string | null;
  campaignId: string | null;
  buyerId: string | null;
  callerAni: string;
  dialedNumber: string | null;
  callerState: string | null;
  callerZip: string | null;
  callerCity: string | null;
  callerAreaCode: string | null;
  startedAt: string; // ISO string
  connectedAt: string | null;
  endedAt: string | null;
  totalDurationSeconds: number | null;
  connectedDurationSeconds: number | null;
  status: string | null;
  hangupInitiator: string | null;
  revenue: string | null; // decimal string
  cost: string | null;
  recordingUrl: string | null;
  trafficSource: string | null;
  publisherId: string | null;
  clickId: string | null;
  createdAt: string;
  classification: CallClassification | null;
}

export interface DetailedCall extends RealCall {
  transcript: CallTranscript | null;
  fraudEvents: FraudEvent[];
  feedback: unknown[];
}

export interface FlaggedCallRow extends RealCall {
  fraudEvent: FraudEvent;
}

export interface SpeakerSegment {
  speaker: string; // "A" or "B" or "agent" / "caller"
  text: string;
  start?: number;
  end?: number;
}

// Utility functions
export function maskAni(ani: string): string {
  if (!ani) return '(unknown)';
  const digits = ani.replace(/\D/g, '');
  if (digits.length === 11 && digits[0] === '1') {
    const area = digits.slice(1, 4);
    const exchange = digits.slice(4, 7);
    const lineEnd = digits.slice(9, 11);
    return `(${area}) ${exchange}-**${lineEnd}`;
  }
  if (digits.length === 10) {
    const area = digits.slice(0, 3);
    const exchange = digits.slice(3, 6);
    const lineEnd = digits.slice(8, 10);
    return `(${area}) ${exchange}-**${lineEnd}`;
  }
  if (ani.length > 4) {
    return ani.slice(0, -4) + '****';
  }
  return ani;
}

export function formatDuration(sec: number | null | undefined): string {
  if (!sec) return '0m 00s';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

export function formatCallTime(isoStr: string | null | undefined): string {
  if (!isoStr) return '—';
  try {
    return new Date(isoStr).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    });
  } catch {
    return isoStr;
  }
}
